import { Can } from '@/domains/access-control';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { useCan } from '@skewb/access-control/react';
import { Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { SkillItem } from '../api/lookupSettingsApi';
import {
  useCreateSkill,
  useDeleteSkill,
  useDisciplinesSettings,
  useSkillsSettings,
  useUpdateSkill,
} from '../hooks/useLookupSettings';

interface FormState {
  name: string;
  disciplineId: string;
}

const emptyForm: FormState = { name: '', disciplineId: '' };

export default function SkillsStep() {
  const [filterDisciplineId, setFilterDisciplineId] = useState<number | undefined>(undefined);
  const { data, isLoading } = useSkillsSettings(filterDisciplineId);
  const { data: disciplinesData } = useDisciplinesSettings();
  const createMutation = useCreateSkill();
  const updateMutation = useUpdateSkill();
  const deleteMutation = useDeleteSkill();
  // Drop the always-empty Actions column when the role can neither edit nor delete (WRK-5669).
  const canEdit = useCan('update', 'Skill');
  const canDelete = useCan('delete', 'Skill');
  const showActions = canEdit || canDelete;

  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const disciplines = disciplinesData?.values ?? [];
  const items = data?.values ?? [];
  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(
      (i) => i.name?.toLowerCase().includes(q) || i.discipline_name?.toLowerCase().includes(q),
    );
  }, [items, search]);

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  }

  function openEdit(item: SkillItem) {
    setEditingId(item.id);
    setForm({
      name: item.name,
      disciplineId: item.discipline_id ? String(item.discipline_id) : '',
    });
    setDialogOpen(true);
  }

  async function handleSave() {
    const body = {
      name: form.name,
      discipline_id: form.disciplineId ? Number(form.disciplineId) : undefined,
    };
    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, ...body });
    } else {
      await createMutation.mutateAsync(body);
    }
    setDialogOpen(false);
  }

  async function handleDelete(id: number) {
    await deleteMutation.mutateAsync(id);
    setDeleteConfirmId(null);
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search skills..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={filterDisciplineId ? String(filterDisciplineId) : 'all'}
            onValueChange={(v) => setFilterDisciplineId(v === 'all' ? undefined : Number(v))}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Disciplines" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Disciplines</SelectItem>
              {disciplines.map((d) => (
                <SelectItem key={d.id} value={String(d.id)}>
                  {d.discipline}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Can action="create" subject="Skill">
            <Button
              onClick={openCreate}
              style={{ backgroundColor: '#E84A2B' }}
              className="text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Skill
            </Button>
          </Can>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Skill Name</TableHead>
              <TableHead>Discipline</TableHead>
              {showActions && <TableHead className="w-[100px]">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={showActions ? 3 : 2}
                  className="text-center py-8 text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={showActions ? 3 : 2}
                  className="text-center py-8 text-muted-foreground"
                >
                  {search ? 'No skills match your search' : 'No skills found'}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {item.discipline_name ?? '—'}
                  </TableCell>
                  {showActions && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Can action="update" subject="Skill">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(item)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </Can>
                        <Can action="delete" subject="Skill">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteConfirmId(item.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </Can>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Skill' : 'Add Skill'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Skill Name</Label>
              <Input
                placeholder="Enter skill name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Discipline</Label>
              <Select
                value={form.disciplineId || 'none'}
                onValueChange={(v) => setForm({ ...form, disciplineId: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a discipline" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Discipline</SelectItem>
                  {disciplines.map((d) => (
                    <SelectItem key={d.id} value={String(d.id)}>
                      {d.discipline}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!form.name.trim() || isSaving}
              style={{ backgroundColor: '#E84A2B' }}
              className="text-white"
            >
              {isSaving ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmId !== null} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Skill</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this skill? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
